import { useCallback, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import TagManager, { DataLayerArgs } from 'react-gtm-module'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { styled } from 'styled-components'
import { debounce } from 'lodash'
import { deleteCart, putCart } from '../../api/cart'
import { postRestock } from '../../api/product'
import { OptionContentContent, OptionContentWrap, OptionPrice, OptionWrap } from '../../components/product/ProductModal'
import { useAppContext } from '../../components/provider/AppProvider'
import { useCartContext } from '../../components/provider/CartProvider'
import { useCommonContext } from '../../components/provider/CommonProvider'
import { useMyContext } from '../../components/provider/MyProvider'
import { AFWebLinkType, handlePostMessage, RNPostMessage, sendSetAppsflyerEvent } from '../../js/postMessage'
import { getParsedLocalStorageItem, getParsedSessionStorageItem, onErrorImg, plusComma } from '../../js/script'
import {
  SET_INVALID_ORDER_VARIATION_MAP,
  SET_ORDER_PRODUCT_MAP,
  SET_ORDER_VARIATION_MAP,
  SET_SELECT_ORDER_VARIATION_MAP,
  SET_TOTAL_CART_PRICE_INFO,
} from './CartGroupProvider'
import useCartGroup from './useCartGroup'

interface CartProductItemProps {
  variation: any
}

export default function CartProductItem(props: CartProductItemProps) {
  const { variation } = props

  const navigate: NavigateFunction = useNavigate()

  const { isAppView } = useAppContext()
  const { hasAccessToken, isLottie } = useCommonContext()
  const { setToastMessage, setIsToast } = useMyContext()

  const { setAddCart } = useCartContext()

  const {
    userProfile,
    userCredit,
    orderProductMap,
    selectOrderVariationMap,
    dispatch,
    createGTMItems,
    fetchCartData,
    generateOrderCartMap,
  } = useCartGroup()

  const variationProductCoupon = useMemo(() => {
    if (Object.keys(selectOrderVariationMap).length === 0) return null
    if (userCredit.productCoupons.length === 0) return null
    return userCredit.productCoupons.find((coupon) => coupon.mallProductNo === variation.productNo)
  }, [selectOrderVariationMap, userCredit, variation.productNo])

  const isSelectedVariation = useMemo(() => {
    return Object.keys(selectOrderVariationMap).includes(`${variation.optionNo}`)
  }, [selectOrderVariationMap, variation.optionNo])

  const hasInvalidOption = useMemo(() => {
    const { valid, orderCntChangeable } = variation.validInfo
    return !valid || !orderCntChangeable
  }, [variation])

  const calcDiscountPercent = useMemo(() => {
    return Math.floor((variation.price.immediateDiscountAmt / variation.price.salePrice) * 100)
  }, [variation])

  const onClickCartItemInfo = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()

      if (isAppView) {
        RNPostMessage(AFWebLinkType.moveToLink, {
          url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/product/${variation.productNo}`,
        })
      } else {
        navigate(`/product/${variation.productNo}`)
      }
    },
    [isAppView, variation.productNo]
  )

  const onClickCartItemCheck = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()

      try {
        const updateSelectOrderVariationMap = { ...selectOrderVariationMap }

        if (Object.keys(selectOrderVariationMap).includes(`${variation.optionNo}`)) {
          delete updateSelectOrderVariationMap[variation.optionNo]
        } else {
          updateSelectOrderVariationMap[variation.optionNo] = {
            ...variation,
          }
        }

        dispatch({
          type: SET_SELECT_ORDER_VARIATION_MAP,
          payload: updateSelectOrderVariationMap,
        })

        // await fetchCartData()
      } catch (error) {
        console.error('장바구니 상품 선택 실패', error)
      }
    },
    [selectOrderVariationMap, variation, fetchCartData]
  )

  const onClickCartItemDelete = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()

      try {
        if (!hasAccessToken) {
          const guestCart = getParsedSessionStorageItem('guestCart')

          if (guestCart) {
            const updateGuestCart = guestCart.filter((item: any) => item.optionNo !== variation.optionNo)
            window.sessionStorage.setItem('guestCart', JSON.stringify(updateGuestCart))
          }
        } else {
          const { data } = await deleteCart(variation.cartNo)

          if (data) {
            const gtmItem = await createGTMItems([variation])
            const gtmDeleteCartItem: DataLayerArgs = {
              dataLayer: {
                event: 'remove_from_cart', // ecommerce 이벤트 중 상품보기 단계
                ecommerce: {
                  currency: 'KRW',
                  value: variation.price.buyAmt,
                  items: gtmItem,
                },
              },
            }

            if (isAppView) {
              sendSetAppsflyerEvent({
                eventName: 'remove_from_cart',
                eventType: {
                  currency: 'KRW',
                  value: variation.price.buyAmt,
                  items: gtmItem,
                },
              })
            } else {
              TagManager.dataLayer(gtmDeleteCartItem)
            }

            const userCart = getParsedSessionStorageItem('cart')
            const storageCartList = getParsedLocalStorageItem('cartList')

            if (userCart) {
              const updateUserCart = userCart?.filter((item: any) => item.optionNo !== variation.optionNo)
              window.sessionStorage.setItem('cart', JSON.stringify(updateUserCart))
            }

            if (storageCartList) {
              const updateStorageCartList = storageCartList?.filter((item: any) => item.product !== variation.productNo)
              window.sessionStorage.setItem('cartlist', JSON.stringify(updateStorageCartList))
            }

            handlePostMessage({
              actionType: 'deleteCartApp',
              params: {
                mobile: isMobile ? 'mobile' : 'pc',
                productId: variation.productNo,
              },
            })

            setAddCart((prev) => !prev)
          }
        }
      } catch (error) {
        console.error('장바구니 상품삭제 실패', error)
      } finally {
        const fetchCartReponse = await fetchCartData()

        if (fetchCartReponse) {
          const { price } = fetchCartReponse
          const cartMapReponse = generateOrderCartMap(fetchCartReponse)
          const {
            orderProductMap,
            orderVariationMap,
            invalidOrderVariationMap,
            selectOrderVariationMap: currentSelectOrderVariationMap,
          } = cartMapReponse
          const prevSelectOrderVariationMap = { ...selectOrderVariationMap }

          dispatch({
            type: SET_ORDER_PRODUCT_MAP,
            payload: orderProductMap,
          })

          dispatch({
            type: SET_ORDER_VARIATION_MAP,
            payload: orderVariationMap,
          })
          dispatch({
            type: SET_INVALID_ORDER_VARIATION_MAP,
            payload: invalidOrderVariationMap,
          })

          const updatedSelectOrderVariationMap = { ...prevSelectOrderVariationMap }
          Object.keys(prevSelectOrderVariationMap).forEach((key) => {
            if (key in currentSelectOrderVariationMap) {
              updatedSelectOrderVariationMap[key] = currentSelectOrderVariationMap[key]
            } else {
              delete updatedSelectOrderVariationMap[key]
            }
          })

          dispatch({
            type: SET_SELECT_ORDER_VARIATION_MAP,
            payload: updatedSelectOrderVariationMap,
          })

          dispatch({
            type: SET_TOTAL_CART_PRICE_INFO,
            payload: price,
          })
        }
      }
    },
    [hasAccessToken, orderProductMap, selectOrderVariationMap, variation]
  )

  const onClickCartItemRestock = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()

      if (!hasAccessToken || !userProfile) return

      if (window.confirm('재입고 알림을 신청하시겠습니까?')) {
        try {
          const response = await postRestock(
            [variation.optionNo],
            userProfile.nickname || '',
            userProfile.mobileNo || ''
          )

          if (response.status === 204) {
            setIsToast(true)
            setToastMessage('재입고 알림 신청이 완료되었습니다.')
          }
        } catch (error) {
          console.error('재입고 알림 신청 실패', error)
          setIsToast(true)
          setToastMessage('이미 재입고 알림을 신청하셨습니다.')
        }
      }
    },
    [hasAccessToken, userProfile, variation]
  )

  const onClickUpdateVariationOrderCnt = useCallback(
    async (count: number) => {
      try {
        if (!hasAccessToken) {
          const guestCart = getParsedSessionStorageItem('guestCart')

          if (guestCart) {
            const updateGuestCart = guestCart?.map((item: any) => {
              if (item.optionNo === variation.optionNo) {
                return { ...item, orderCnt: count }
              }
              return item
            })
            window.sessionStorage.setItem('guestCart', JSON.stringify(updateGuestCart))
          }
        } else {
          const response = await putCart(variation.cartNo, count)

          if (response.status === 204) {
            const userCart = getParsedSessionStorageItem('cart')

            if (userCart) {
              const updateUserCart = userCart?.map((item: any) => {
                if (item.optionNo === variation.optionNo) {
                  return { ...item, orderCnt: count }
                }
                return item
              })
              window.sessionStorage.setItem('cart', JSON.stringify(updateUserCart))
            }
          }
        }
      } catch (error) {
        console.error('장바구니 수량 변경 실패', error)
      } finally {
        const fetchCartReponse = await fetchCartData()
        const prevSelectOrderVariationMap = { ...selectOrderVariationMap }

        if (fetchCartReponse) {
          const { price } = fetchCartReponse
          const cartMapReponse = generateOrderCartMap(fetchCartReponse)
          const {
            orderProductMap,
            orderVariationMap,
            invalidOrderVariationMap,
            selectOrderVariationMap: currentSelectOrderVariationMap,
          } = cartMapReponse

          dispatch({
            type: SET_ORDER_PRODUCT_MAP,
            payload: orderProductMap,
          })

          dispatch({
            type: SET_ORDER_VARIATION_MAP,
            payload: orderVariationMap,
          })
          dispatch({
            type: SET_INVALID_ORDER_VARIATION_MAP,
            payload: invalidOrderVariationMap,
          })

          if (variation.optionNo in prevSelectOrderVariationMap) {
            // 선택된 상품의 수량이 변경되었을 때
            // prevSelectOrderVariationMap의 key만 currentSelectOrderVariationMap에서 value를 업데이트
            const updatedSelectOrderVariationMap = { ...prevSelectOrderVariationMap }
            Object.keys(prevSelectOrderVariationMap).forEach((key) => {
              if (key in currentSelectOrderVariationMap) {
                updatedSelectOrderVariationMap[key] = currentSelectOrderVariationMap[key]
              }
            })
            dispatch({
              type: SET_SELECT_ORDER_VARIATION_MAP,
              payload: updatedSelectOrderVariationMap,
            })
          } else {
            // 선택되지 않은 상품의 수량이 변경되었을 때
            dispatch({
              type: SET_SELECT_ORDER_VARIATION_MAP,
              payload: {
                ...prevSelectOrderVariationMap,
                [variation.optionNo]: currentSelectOrderVariationMap[variation.optionNo],
              },
            })
          }

          dispatch({
            type: SET_TOTAL_CART_PRICE_INFO,
            payload: price,
          })
        }
      }
    },
    [hasAccessToken, variation, selectOrderVariationMap, fetchCartData]
  )

  // 수량 변경 debounce
  const debouncedUpdateOrderCnt = useMemo(
    () =>
      debounce((count: number) => {
        onClickUpdateVariationOrderCnt(count)
      }, 300),
    [onClickUpdateVariationOrderCnt]
  )

  return (
    <CartItemWrapper>
      {/* 브랜드명 */}
      {variation.brandName && <CartBrandTitle>{variation.brandName}</CartBrandTitle>}

      {/* 상품 정보 */}

      {/* 장바구니 상품 옵션 선택 버튼 */}
      <CartItemContainer>
        <CartItemCheckButton
          disabled={hasInvalidOption}
          className={isSelectedVariation ? 'selected' : ''}
          onClick={onClickCartItemCheck}
        />
        <CartItemInfo onClick={onClickCartItemInfo}>
          {/* 상품 사진 */}
          <CartItemImage>
            {hasInvalidOption && <InvalidDim />}
            <img src={variation.imageUrl} alt={variation.productName} onError={onErrorImg} />
          </CartItemImage>
          {/* 상품 텍스트 정보 */}
          <CartItemText>
            <div className='name'>{variation.productName}</div>
            {calcDiscountPercent > 0 && (
              <div className='sale_price'>{variation.price.salePrice ? plusComma(variation.price.salePrice) : 0}원</div>
            )}
            <div className='discount_info'>
              {calcDiscountPercent > 0 && <div className='discount_percent'>{calcDiscountPercent}%</div>}
              <div className='discount_price'>
                {plusComma(variation.price.salePrice - variation.price.immediateDiscountAmt)}원
              </div>
            </div>
            <div className='cart_item_status'>
              {/* 쿠폰 사용 가능 */}
              {variationProductCoupon && variationProductCoupon.discountAmt > 0 ? (
                <label className='status_label coupon'>{variationProductCoupon.productCouponName}</label>
              ) : (
                variation.couponUsable && <label className='status_label coupon'>쿠폰 사용 가능</label>
              )}
              {/* 적립금 사용 가능 */}
              {hasAccessToken && variation.accumulationUsable && (
                <label className='status_label accumulation'>적립금 사용 가능</label>
              )}
            </div>
          </CartItemText>
        </CartItemInfo>
        {/* 장바구니 상품 삭제 버튼 */}
        <CartItemDeleteButton onClick={onClickCartItemDelete}>
          <img src='/images/icon_close_gray.svg' alt='장바구니 상품 삭제 아이콘' />
        </CartItemDeleteButton>
      </CartItemContainer>
      {/* 옵션 */}
      <OptionWrap>
        <CartItemOptionInfo>
          <OptionContentContent>옵션 : {variation.optionValue}</OptionContentContent>
          {hasAccessToken && hasInvalidOption && (
            <CartItemRestockButton onClick={onClickCartItemRestock}>
              <strong>재입고 알림 받기</strong>
              <img src='/images/icon_noti.png' alt='재입고 알림' />
            </CartItemRestockButton>
          )}
        </CartItemOptionInfo>
        <CartItemOptionInfo>
          <BtnGroupQuantity>
            <CartItemButton
              type='button'
              disabled={isLottie || hasInvalidOption || variation.orderCnt === 1}
              onClick={(event) => {
                event.stopPropagation()
                debouncedUpdateOrderCnt(variation.orderCnt - 1)
              }}
            >
              <img src={`/images/icon_minus2.svg`} alt='감소' />
            </CartItemButton>
            <span className={hasInvalidOption ? 'disabled' : ''}>{variation.orderCnt}</span>
            <CartItemButton
              type='button'
              disabled={isLottie || hasInvalidOption || variation.orderCnt >= variation.stockCnt}
              onClick={(event) => {
                event.stopPropagation()
                debouncedUpdateOrderCnt(variation.orderCnt + 1)
              }}
            >
              {<img src={`/images/icon_plus2.svg`} alt='증가' />}
            </CartItemButton>
          </BtnGroupQuantity>
          <OptionPrice>{plusComma(variation.price.buyAmt)}원</OptionPrice>
        </CartItemOptionInfo>
      </OptionWrap>
    </CartItemWrapper>
  )
}

const CartItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  margin-bottom: 20px;
  @media screen and (min-width: 768px) {
    gap: 20px;
    margin-bottom: 32px;
  }
`
const CartItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
`
// 장바구니 브랜드명
export const CartBrandTitle = styled.strong`
  color: var(--colBlack);
  font-weight: 600;
  font-size: 18px;
  line-height: 1.2;
`
const CartItemButton = styled.button`
  outline: none;
  border: none;
  background: none;
  cursor: pointer;
  &:disabled {
    opacity: 0.2;
    cursor: not-allowed;
  }
`
// 장바구니 옵션 삭제 버튼
const CartItemDeleteButton = styled(CartItemButton)`
  padding: 2px;
`
// 장바구니 상품 옵션 선택 버튼
const CartItemCheckButton = styled(CartItemButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  min-width: 28px;
  height: 28px;
  position: relative;
  border-radius: 50%;
  border-width: 1px;
  border-style: solid;
  background-color: var(--colWhite);
  border-color: var(--colBlack);
  &:after {
    content: '';
    display: none;
    width: 8px;
    height: 4px;
    margin-top: -1px;
    border-left: 2px solid var(--colBlack);
    border-bottom: 2px solid var(--colBlack);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
  }
  &.selected {
    background-color: var(--colBlack);
    border-color: var(--colBlack);
    &:after {
      display: block;
      border-color: var(--colWhite);
    }
  }
  &:disabled {
    background-color: var(--colGray4);
    border-color: var(--colGray4);
    cursor: not-allowed;
    opacity: 1;
  }
`

// 장바구니 상품 재입고 버튼
export const CartItemRestockButton = styled(CartItemButton)`
  display: flex;
  align-items: center;
  gap: 3px;
  > strong {
    line-height: 1.2;
    font-weight: 500;
    font-size: 13px;
    color: var(--colPointBlue);
  }
  > img {
    width: 20px;
    height: 20px;
  }
`

// 장바구니 상품정보
export const CartItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 10px;
  position: relative;
  cursor: pointer;
`
// 장바구니 상품정보 - 이미지
export const CartItemImage = styled.div`
  overflow: hidden;
  position: relative;
  width: 80px;
  min-width: 80px;
  height: 80px;
  border-radius: 6px;
  img {
    width: 100%;
    height: 100%;
  }
  &:after {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    background: var(--colDimBlack);
    opacity: 0.03;
    left: 0;
    top: 0;
  }
  @media screen and (min-width: 768px) {
    width: 100px;
    height: 100px;
  }
`
export const InvalidDim = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2;
  &:before,
  &:after {
    content: '';
    display: block;
    width: 141.4%;
    height: 1px;
    background-color: var(--colWhite);
    position: absolute;
    top: 50%;
    left: 50%;
    transform-origin: center;
  }
  &:before {
    transform: translate(-50%, -50%) rotate(45deg);
  }
  &:after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`
// 장바구니 상품정보 - 텍스트
export const CartItemText = styled.div`
  position: relative;
  .cart_item_status {
    display: flex;
    margin-top: 4px;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }
  .status_label {
    overflow: hidden;
    display: inline-block;
    max-width: 88px;
    padding: 4px;
    font-size: 12px;
    font-weight: 500;
    color: var(--colWhite);
    border-radius: 4px;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: center;
    &.coupon {
      background-color: var(--colPointBlue);
    }
    &.accumulation {
      background-color: var(--colPointOrange);
    }
  }
  .name {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.25;
    @media screen and (min-width: 768px) {
      font-size: 16px;
    }
  }
  .sale_price {
    line-height: 1.3;
    font-size: 12px;
    font-weight: 500;
    color: var(--colGray5);
    text-decoration: line-through;
    margin-bottom: 2px;
    @media screen and (min-width: 768px) {
      font-size: 14px;
    }
  }
  .discount_info {
    display: flex;
    gap: 6px;
    line-height: 1.2;
    font-size: 15px;
    font-weight: 600;
    @media screen and (min-width: 768px) {
      font-size: 18px;
    }
    .discount_percent {
      color: var(--colPointOrange);
    }
  }
`

const CartItemOptionInfo = styled(OptionContentWrap)`
  & + div {
    margin-top: 12px;
  }
`

const BtnGroupQuantity = styled.div`
  overflow: hidden;
  display: flex;
  height: 32px;
  align-items: center;
  background-color: var(--colWhite);
  border-radius: 4px;
  border: 1px solid var(--colGray4);
  > * {
    height: 100%;
  }
  > button {
    width: 30px;
    padding: 7px 8px;
  }
  > span {
    display: flex;
    width: 40px;
    justify-content: center;
    align-items: center;
    line-height: 1;
    font-weight: 700;
    font-size: 16px;
    border-left: 1px solid var(--colGray4);
    border-right: 1px solid var(--colGray4);
    &.disabled {
      color: var(--colGray5);
    }
  }
`
